import React, { useState, useEffect } from 'react';
import { api } from './api/api';

// ── Helpers ────────────────────────────────────────────────────────────────

const val = (v) => v ?? '—';

const RainBadge = () => (
    <span style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, background: '#1d4ed8', color: '#fff', borderRadius: 3, padding: '1px 4px', marginRight: 6, verticalAlign: 'middle', letterSpacing: 0.3 }}>
        RAIN
    </span>
);

const WeatherBadge = ({ label, isDry }) => (
    <span style={{
        display: 'inline-block', fontSize: 11, fontWeight: 600,
        background: isDry ? '#fef9c3' : '#eff6ff',
        color: isDry ? '#854d0e' : '#1d4ed8',
        border: `1px solid ${isDry ? '#fde68a' : '#bfdbfe'}`,
        borderRadius: 4, padding: '2px 8px',
    }}>
        {label}
    </span>
);

// ── Weather stint timeline ─────────────────────────────────────────────────

export const StintTimeline = ({ stints }) => {
    if (!stints || stints.length === 0) return null;
    const totalLaps = stints[stints.length - 1].lapFinish;

    return (
        <div>
            {/* Bar */}
            <div style={{ display: 'flex', height: 20, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)' }}>
                {stints.map((s, i) => {
                    const width = ((s.lapFinish - s.lapStart + 1) / totalLaps) * 100;
                    return (
                        <div
                            key={i}
                            title={`Laps ${s.lapStart}–${s.lapFinish}: ${s.dry ? 'Dry' : 'Rain'}`}
                            style={{
                                width: `${width}%`,
                                background: s.dry ? '#fef08a' : '#93c5fd',
                                borderRight: i < stints.length - 1 ? '1px solid rgba(0,0,0,0.12)' : 'none',
                            }}
                        />
                    );
                })}
            </div>
            {/* Labels */}
            <div style={{ display: 'flex', marginTop: 4 }}>
                {stints.map((s, i) => {
                    const width = ((s.lapFinish - s.lapStart + 1) / totalLaps) * 100;
                    return (
                        <div key={i} style={{ width: `${width}%`, fontSize: 10, color: 'var(--c-text-muted)', paddingLeft: 2 }}>
                            {s.lapStart}–{s.lapFinish}
                            <span style={{ marginLeft: 3, color: s.dry ? '#854d0e' : '#1d4ed8', fontWeight: 600 }}>
                                {s.dry ? 'Dry' : 'Rain'}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Race detail ────────────────────────────────────────────────────────────

const RaceDetail = ({ detail }) => {
    const QUARTERS = [
        { label: 'Q1', tLow: 'raceQ1TemperatureLow', tHigh: 'raceQ1TemperatureHigh', rLow: 'raceQ1RainLow', rHigh: 'raceQ1RainHigh', hLow: 'raceQ1HumidityLow', hHigh: 'raceQ1HumidityHigh' },
        { label: 'Q2', tLow: 'raceQ2TemperatureLow', tHigh: 'raceQ2TemperatureHigh', rLow: 'raceQ2RainLow', rHigh: 'raceQ2RainHigh', hLow: 'raceQ2HumidityLow', hHigh: 'raceQ2HumidityHigh' },
        { label: 'Q3', tLow: 'raceQ3TemperatureLow', tHigh: 'raceQ3TemperatureHigh', rLow: 'raceQ3RainLow', rHigh: 'raceQ3RainHigh', hLow: 'raceQ3HumidityLow', hHigh: 'raceQ3HumidityHigh' },
        { label: 'Q4', tLow: 'raceQ4TemperatureLow', tHigh: 'raceQ4TemperatureHigh', rLow: 'raceQ4RainLow', rHigh: 'raceQ4RainHigh', hLow: 'raceQ4HumidityLow', hHigh: 'raceQ4HumidityHigh' },
    ];

    const range = (lo, hi) => {
        if (lo == null && hi == null) return '—';
        if (lo === hi) return `${lo}`;
        return `${lo}–${hi}`;
    };

    return (
        <div>
            {/* Qualifying */}
            <div className="fb-card">
                <div className="fb-card-header"><span className="fb-card-title">Qualifying Weather</span></div>
                <div className="fb-table-wrap">
                    <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
                        <thead>
                            <tr>
                                <th>Session</th>
                                <th>Condition</th>
                                <th>Temperature (°C)</th>
                                <th>Humidity (%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ fontWeight: 600 }}>Q1</td>
                                <td><WeatherBadge label={val(detail.q1Weather)} isDry={detail.isQ1Dry} /></td>
                                <td>{val(detail.q1Temperature)}°C</td>
                                <td>{val(detail.q1Humidity)}%</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 600 }}>Q2</td>
                                <td><WeatherBadge label={val(detail.q2Weather)} isDry={detail.isQ2Dry} /></td>
                                <td>{val(detail.q2Temperature)}°C</td>
                                <td>{val(detail.q2Humidity)}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Race quarters */}
            <div className="fb-card">
                <div className="fb-card-header"><span className="fb-card-title">Race Quarters</span></div>
                <div className="fb-table-wrap">
                    <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
                        <thead>
                            <tr>
                                <th>Quarter</th>
                                <th>Temperature (°C)</th>
                                <th>Rain (%)</th>
                                <th>Humidity (%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {QUARTERS.map(q => (
                                <tr key={q.label}>
                                    <td style={{ fontWeight: 600 }}>{q.label}</td>
                                    <td>{range(detail[q.tLow], detail[q.tHigh])}</td>
                                    <td>
                                        <span style={{ color: detail[q.rLow] > 0 || detail[q.rHigh] > 0 ? '#1d4ed8' : 'inherit' }}>
                                            {range(detail[q.rLow], detail[q.rHigh])}
                                        </span>
                                    </td>
                                    <td>{range(detail[q.hLow], detail[q.hHigh])}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Stints timeline */}
            {detail.weather && detail.weather.length > 0 && (
                <div className="fb-card">
                    <div className="fb-card-header">
                        <span className="fb-card-title">Race Weather Stints</span>
                        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                            <span style={{ background: '#fef08a', padding: '1px 6px', borderRadius: 3, marginRight: 6 }}>Dry</span>
                            <span style={{ background: '#93c5fd', padding: '1px 6px', borderRadius: 3 }}>Rain</span>
                        </span>
                    </div>
                    <div className="fb-card-body">
                        <StintTimeline stints={detail.weather} />
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const WeatherHistory = () => {
    const [raceList,      setRaceList]      = useState([]);
    const [selectedRace,  setSelectedRace]  = useState(null);
    const [detail,        setDetail]        = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error,         setError]         = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get('/weather/races');
                setRaceList(res.data.map(r => ({ ...r, key: `${r.season}_${r.raceNumber}` })));
            } catch (err) {
                console.error(err);
                setError('Failed to load races.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const selectRace = async (race) => {
        setSelectedRace(race);
        setDetailLoading(true);
        try {
            const res = await api.get(`/weather/${race.season}/${race.raceNumber}`);
            setDetail(res.data);
        } catch (err) {
            console.error(err);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleMobileSelect = (e) => {
        const race = raceList.find(r => r.key === e.target.value);
        if (race) selectRace(race);
    };

    // Group by season for sidebar
    const seasons = [...new Set(raceList.map(r => r.season))].sort((a, b) => b - a);
    const bySeason = seasons.reduce((acc, s) => {
        acc[s] = raceList.filter(r => r.season === s);
        return acc;
    }, {});

    const S = {
        sidebar: { width: 240, borderRight: '1px solid var(--c-border)', overflowY: 'auto', flexShrink: 0, background: '#fff' },
        sidebarHeader: { padding: '12px 16px', fontWeight: 700, fontSize: 11, borderBottom: '1px solid var(--c-border)', background: '#fafafa', textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--c-text-muted)' },
        seasonLabel: { padding: '12px 16px 4px', fontSize: 10, fontWeight: 700, color: 'var(--c-text-light)', textTransform: 'uppercase', letterSpacing: 1.2 },
        raceItem: (sel) => ({ padding: '9px 16px', cursor: 'pointer', background: sel ? '#fff3f3' : 'transparent', borderLeft: `3px solid ${sel ? 'var(--c-accent)' : 'transparent'}`, fontSize: 13, fontWeight: sel ? 600 : 400, color: 'var(--c-text)', transition: 'background 0.12s' }),
        main: { flex: 1, overflowY: 'auto', padding: 24, background: 'var(--c-bg)', minWidth: 0 },
        placeholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--c-text-light)', gap: 12 },
    };

    return (
        <div className="ra-page">
            {/* Sidebar */}
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Races</div>
                {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
                {error   && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
                {!loading && !error && raceList.length === 0 && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>No data.</div>}
                {seasons.map(season => (
                    <div key={season}>
                        <div style={S.seasonLabel}>Season {season}</div>
                        {bySeason[season].map(race => (
                            <div key={race.key} style={S.raceItem(selectedRace?.key === race.key)} onClick={() => selectRace(race)}>
                                {race.rain && <RainBadge />}
                                R{race.raceNumber}{race.trackName ? ` — ${race.trackName}` : ''}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Main */}
            <div style={S.main} className="ra-main">
                <div className="ra-mobile-selector">
                    <select className="fb-select" style={{ width: '100%' }} value={selectedRace?.key || ''} onChange={handleMobileSelect} disabled={loading}>
                        <option value="">{loading ? 'Loading races…' : '— Select a race —'}</option>
                        {seasons.map(season => (
                            <optgroup key={season} label={`Season ${season}`}>
                                {bySeason[season].map(race => (
                                    <option key={race.key} value={race.key}>{race.label}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                {!selectedRace && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>🌤️</span>
                        <span style={{ fontSize: 14 }}>Select a race to view weather data</span>
                    </div>
                )}

                {selectedRace && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>Loading weather…</div>
                )}

                {selectedRace && !detailLoading && detail && (
                    <>
                        <div className="fb-page-header">
                            <div className="fb-page-title">
                                {selectedRace.rain && <RainBadge />}
                                Race {selectedRace.raceNumber}{selectedRace.trackName ? ` — ${selectedRace.trackName}` : ''}
                            </div>
                            <div className="fb-page-sub">Season {selectedRace.season}</div>
                        </div>
                        <RaceDetail detail={detail} />
                    </>
                )}

                {selectedRace && !detailLoading && !detail && (
                    <div style={{ color: 'var(--c-text-muted)', fontSize: 14, marginTop: 24 }}>No weather data available for this race.</div>
                )}
            </div>
        </div>
    );
};

export default WeatherHistory;
